<template>
  <div>
    <load-profile />
    <b-card>
      <validation-observer
        ref="editForm"
        #default="{ invalid }"
      >
        <n-form-confirmation
          key="editForm"
          :form="$refs.editForm"
          :disabled="invalid"
          @submit="validateForm"
        >
          <n-input
            v-model="data"
            :fields="
              $route.query.isBot + '' === 'true'
                ? botFields
                : fieldsGroupComputed
            "
            :init-value="initData"
          >
            <template #isActive>
              <div v-if="data.isActive == 2" />
            </template>
          </n-input>

          <b-row v-if="!($route.query.isBot + '' === 'true')">
            <div class="compact-container">
              <p class="mb-0 font-weight-bold">
                {{ $t('general.department_and_team') }}
              </p>
              <div
                v-for="(param, paramIndex) in data.departmentAndTeam"
                :key="paramIndex"
                class="compact-card"
              >
                <div class="key">
                  <validation-provider
                    v-slot="{ errors }"
                    :name="$t('breadcrumb.department')"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t('breadcrumb.department')"
                      class="mt-1"
                      style="color: #6e6b7b"
                    >
                      <n-async-single-select
                        v-model="param.departmentId"
                        repository="department"
                        selection-key="id"
                        clearable
                        :placeholder="$t('breadcrumb.department')"
                        selection-label="translations"
                        :state="errors.length > 0 ? 'is-invalid' : null"
                        :must-excluded-ids="
                          data.departmentAndTeam
                            .map(item => item.departmentId)
                            .filter(id => id !== param.departmentId)
                        "
                        :init-options="param.departmentId"
                        :immediate="true"
                        @input="param.teamId = []"
                      />
                    </b-form-group>
                  </validation-provider>
                </div>
                <div class="value">
                  <validation-provider
                    v-slot="{ errors }"
                    :name="$t('breadcrumb.team')"
                  >
                    <b-form-group
                      class="mt-1"
                      style="color: #6e6b7b"
                    >
                      <div class="col-form-label pt-0">
                        <span>{{ $t('breadcrumb.team') }}</span>
                        <span class="text-muted">
                          - {{ $t('field.optional') }}</span>
                      </div>
                      <n-async-multi-select
                        :key="param.departmentId"
                        v-model="param.teamId"
                        repository="team"
                        selection-key="id"
                        :placeholder="$t('breadcrumb.team')"
                        selection-label="translations"
                        :readonly="!param.departmentId"
                        always-fresh
                        :query="{
                          departmentId: param.departmentId
                        }"
                        :init-options="param.teamId"
                        :state="errors.length > 0 ? 'is-invalid' : null"
                        :immediate="true"
                        @input="onChange"
                      />
                    </b-form-group>
                  </validation-provider>
                </div>
                <div
                  v-if="data.departmentAndTeam.length > 1"
                  class="delete-btn"
                >
                  <b-button
                    variant="danger"
                    pill
                    size="sm"
                    style="padding: 5px"
                    @click="removeParameter(paramIndex)"
                  >
                    <feather-icon icon="TrashIcon" />
                  </b-button>
                </div>
              </div>

              <b-button
                variant="outline-secondary"
                size="sm"
                style="width: fit-content"
                @click="addParameter()"
              >
                <feather-icon icon="PlusIcon" />
                {{ $t('button.add') }}
              </b-button>
            </div>

            <div class="compact-container">
              <div class="switch-container operator">
                <validation-provider>
                  <b-form-group class="mt-1">
                    <b-form-checkbox
                      v-model="data.isAllowedOperatingService"
                      :value="true"
                      :unchecked-value="false"
                      :switch="true"
                    >
                      {{ $t('field.allow_operating_service') }}
                    </b-form-checkbox>
                    <small class="text-muted">{{
                      $t('field.allow_operating_service_hint')
                    }}</small>
                  </b-form-group>
                </validation-provider>
                <div
                  v-if="data.isAllowedOperatingService"
                  class="operation-container"
                >
                  <div style="display: flex; gap: 1rem;">
                    <b-col cols="6">
                      <validation-provider
                        v-slot="{ errors }"
                        :name="$t('breadcrumb.project')"
                        rules="required"
                      >
                        <b-form-group
                          style="color: #6e6b7b"
                        >
                          <div class="col-form-label pt-0">
                            <span>{{ $t('breadcrumb.project') }}</span>
                          </div>
                          <n-async-multi-select
                            v-model="data.projects"
                            repository="project"
                            selection-key="id"
                            :placeholder="$t('breadcrumb.project')"
                            selection-label="translations"
                            is-object
                            always-fresh
                            :state="errors.length > 0 ? 'is-invalid' : null"
                            :immediate="false"
                            clearable
                            included-all
                            :init-options="data.projects.map(el => el.id)"
                            all-option-value="ALL_PROJECT"
                          />
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </div>

                  <div class="compact-container">
                    <p class="mb-0 font-weight-bold">
                      {{ $t('general.service_category_and_type') }}
                    </p>
                    <div
                      v-for="(param, paramIndex) in data.serviceCateAndType"
                      :key="paramIndex"
                      class="service-compact-card"
                    >
                      <div class="key">
                        <validation-provider
                          v-slot="{ errors }"
                          :name="`param${paramIndex}Name`"
                          rules="required"
                        >
                          <b-form-group
                            class="mt-1"
                            style="color: #6e6b7b"
                          >
                            <div class="col-form-label pt-0">
                              <span>{{
                                $t('breadcrumb.serviceCategory')
                              }}</span>
                            </div>
                            <n-async-multi-select
                              v-model="param.serviceCategory"
                              clearable
                              repository="serviceCategory"
                              :placeholder="$t('breadcrumb.serviceCategory')"
                              selection-key="code"
                              selection-label="translations"
                              :is-object="true"
                              :state="errors.length > 0 ? 'is-invalid' : null"
                              :immediate="false"
                              always-fresh
                              included-all
                              no-excluded
                              :init-options="(param.serviceCategory || []).map(el => el.code)"
                              all-option-value="ALL_CATEGORY"
                              @input="onCategoryChange(paramIndex, param.serviceCategory)"
                            />
                          </b-form-group>
                        </validation-provider>
                        <span class="value" />
                      </div>
                      <div class="value">
                        <div class="service-type-container">
                          <validation-provider
                            v-slot="{ errors }"
                            :name="`param${paramIndex}Key`"
                            rules="required"
                          >
                            <b-form-group
                              class="mt-1"
                              style="color: #6e6b7b"
                            >
                              <div class="col-form-label pt-0">
                                <span>{{ $t('breadcrumb.serviceType') }}</span>
                              </div>
                              <n-async-multi-select
                                :key="resetIndex"
                                v-model="param.serviceTypes"
                                repository="serviceType"
                                selection-key="code"
                                clearable
                                :placeholder="$t('breadcrumb.serviceType')"
                                selection-label="translations"
                                :state="errors.length > 0 ? 'is-invalid' : null"
                                :readonly="
                                  !param.serviceCategory ||
                                    (Array.isArray(param.serviceCategory) &&
                                      param.serviceCategory.length === 0)
                                "
                                :is-object="true"
                                included-all
                                all-option-value="ALL_TYPE"
                                :query="getQuery(param.serviceCategory)"
                                always-fresh
                                :init-options="param.serviceTypes.map(el => el.code)"
                                :immediate="false"
                                group-key="service"
                                @input="onServiceTypeChange(paramIndex)"
                              />
                            </b-form-group>
                          </validation-provider>
                          <validation-provider
                            v-slot="{ errors }"
                            :name="`param${paramIndex}Key`"
                            rules="required"
                          >
                            <b-form-group
                              class="mt-1"
                              style="color: #6e6b7b"
                            >
                              <div class="col-form-label pt-0">
                                <span>{{ $t('breadcrumb.serviceSubType') }}</span>
                              </div>
                              <n-async-multi-select
                                :key="resetSubIndex"
                                v-model="param.serviceSubTypes"
                                repository="serviceSubType"
                                selection-key="code"
                                selection-label="translations"
                                clearable
                                :placeholder="$t('breadcrumb.serviceSubType')"
                                :state="errors.length > 0 ? 'is-invalid' : null"
                                :readonly="!param.serviceTypes || (Array.isArray(param.serviceTypes) && param.serviceTypes.length === 0)"
                                :is-object="true"
                                included-all
                                all-option-value="ALL_SUBTYPE"
                                :init-options="param.serviceSubTypes.map(el => el.code)"
                                :query="getSubTypeQuery(param.serviceCategory, param.serviceTypes)"
                                always-fresh
                                group-key="type"
                                :immediate="false"
                              />
                            </b-form-group>
                          </validation-provider>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="compact-container">
                    <div class="switch-container">
                      <validation-provider>
                        <b-form-group>
                          <b-form-checkbox
                            v-model="data.isAutoAssignService"
                            :value="true"
                            :unchecked-value="false"
                            :switch="true"
                          >
                            {{ $t('field.allow_auto_assign_service') }}
                          </b-form-checkbox>
                          <small class="text-muted">{{
                            $t('field.allow_auto_assign_service_hint')
                          }}</small>
                        </b-form-group>
                      </validation-provider>
                    </div>

                    <b-col
                      v-if="data.isAutoAssignService"
                      cols="6"
                    >
                      <validation-provider
                        v-slot="{ errors }"
                        :name="$t('field.serviceCap')"
                        rules="required|numeric|min_value:0"
                      >
                        <b-form-group
                          :label="$t('field.serviceCap')"
                        >
                          <b-form-input
                            v-model="data.totalServiceCap"
                            :placeholder="$t('field.serviceCap')"
                            :state="errors.length > 0 ? false : null"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </div>
                </div>
              </div>
            </div>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              class="text-right"
            >
              <n-button-loading
                v-if="
                  $can('update', permissionType) &&
                    data.type != 'default-merchant'
                "
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="submit"
                class="mt-1 mr-1"
                variant="primary"
                :loading="loading"
                :disabled="invalid || !isValidDepartments"
              >
                {{ $t('button.saveChanges') }}
              </n-button-loading>
              <n-button-loading
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-secondary"
                class="mt-1"
                @submit="back"
              >
                {{ $t('button.cancel') }}
              </n-button-loading>
            </b-col>
          </b-row>
        </n-form-confirmation>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BCard,
  BCardText,
  BLink,
  BFormRadioGroup,
  BButton,
  BFormCheckbox,
} from 'bootstrap-vue'
import Repository from '@/repositories/RepositoryFactory'
import Ripple from 'vue-ripple-directive'
import NButtonLoading from '@/components/NButtonLoading'
import NFormConfirmation from '@/components/NFormConfirmation'
import NInput from '@/components/NInput'
import { bus } from '@/libs/bus'
import useJwt from '@/auth/jwt/useJwt'
import NAsyncSingleSelect from '@/components/NAsyncSingleSelect'
import NAsyncMultiSelect from '@/components/NAsyncMultiSelect'
import { mapValidationMessage } from '@/libs/helper'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'

import languageEn from '@/data/languages/en'
import languageKm from '@/data/languages/km'
import languageZh from '@/data/languages/zh-cn'
import languageVi from '@/data/languages/vi'
import formInputBot from './updateBotFormInput'
import formInput from './updateFormInput'

const OperatorRepository = Repository.get('operator')
const FileRepository = Repository.get('file')

export default {
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BCard,
    BCardText,
    BLink,
    BFormRadioGroup,
    BButton,
    NButtonLoading,
    NFormConfirmation,
    NInput,
    NAsyncSingleSelect,
    NAsyncMultiSelect,
    FeatherIcon,
    BFormCheckbox,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      data: {
        firstName: null,
        lastName: null,
        email: null,
        phone: null,
        roleId: null,
        isEnable: 1,
        newPassword: null,
        supportedCallLanguages: null,
        serviceCap: '5',
        totalServiceCap: '5',
        projects: [],
        departmentAndTeam: [
          {
            departmentId: '',
            teamId: [],
          },
        ],
        serviceCateAndType: [
          {
            name: [],
            serviceCategory: null,
            serviceTypes: [],
            serviceSubTypes: [],
          },
        ],
        isAllowedOperatingService: false,
        isAutoAssignService: false,
        initServiceCategory: [],
        initServiceTypes: [],
        initServiceSubTypes: [],
      },
      initData: {},
      permissions: [],
      loading: false,
      file: null,
      resetIndex: 0,
      initialTotalServiceCap: 5,
      resetSubIndex: 0,
    }
  },
  computed: {
    isValidDepartments() {
      return this.data.departmentAndTeam.every(item => item.departmentId !== '' && item.departmentId !== null)
    },
    isTelegramUser() {
      return !this.data.email
    },
    fieldsGroupComputed() {
      const modifiedFields = this.fields.map(element => {
        if (element.key == 'roleId' || element.key == 'isActive') {
          if (this.userData?.id == this.$route.params.id) {
            element.disabled = true
          } else {
            element.disabled = false
          }
        } else if (element.key === 'supportedCallLanguages') {
          element.options = this.locales.map(e => ({
            ...e,
            label: `${e.emoji} ${e.name}`,
          }))
        }
        return element
      })

      if (this.isTelegramUser) {
        return modifiedFields.filter(field => field.key !== 'email'
          && field.key !== 'newPassword'
          && field.key !== 'retypeNewPassword'
          && field.key !== 'serviceCap')
      }

      return modifiedFields
    },
    userData() {
      return this.$store.state.profile?.email || this.$store.state.profile?.phone
        ? this.$store.state.profile
        : JSON.parse(
          localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName),
        )
    },
    dataExist() {
      return !!this.data
    },
    locales() {
      if (this.$i18n.locale === 'km') {
        return this.languageKm
      }
      if (this.$i18n.locale === 'en') {
        return this.languageEn
      }
      if (this.$i18n.locale === 'vi') {
        return this.languageVi
      }

      return this.languageZh
    },
  },
  created() {
    bus.$on('notificationClick', type => {
      if (type == 'operator' && this.$route.params.id != this.data.id) {
        this.show()
        this.fields = formInput.map(element => {
          if (this.userData?.id == this.$route.params.id) {
            element.disabled = true
          } else {
            element.disabled = false
          }
          return element
        })
      }
    })
  },
  beforeDestroy() {
    bus.$off('notificationClick')
  },
  mounted() {
    this.show()
  },
  methods: {
    getQuery(data) {
      if (!data || data?.length === 2 || data?.some(e => e.code === 'ALL_CATEGORY')) return {}
      return {
        serviceCategoryCode: (data || []).map(e => e.code).join(','),
      }
    },
    getSubTypeQuery(serviceCategoryCode, serviceTypeCode) {
      return {
        serviceCategoryCodes: (serviceCategoryCode || []).map(e => e.code).join(','),
        serviceTypeCodes: (serviceTypeCode || []).map(e => e.code).join(','),
      }
    },
    mustExcludedIds(data) {
      const excludedIds = this.data.serviceCateAndType.map(e => {
        if (typeof e.serviceCategory === 'string') {
          return e.serviceCategory
        }
        return e.serviceCategory ? e.serviceCategory.code : ''
      })

      return excludedIds.filter(e => {
        if (typeof data === 'string') {
          return e !== data
        }
        return e !== data?.code
      })
    },
    getKey(data, index) {
      return typeof data === 'string' ? data : `key-${index}`
    },
    onImageChange(file) {
      this.file = file
    },
    createDefaultParameter(key = 'departmentAndTeam') {
      if (key === 'serviceCateAndType') {
        return {
          serviceCategory: null,
          serviceTypes: [],
          serviceSubTypes: [],
        }
      }
      return {
        departmentId: '',
        teamId: [],
      }
    },
    addParameter(key = 'departmentAndTeam') {
      this.data[key].push(this.createDefaultParameter(key))
    },
    removeParameter(paramIndex, key = 'departmentAndTeam') {
      if (this.data[key].length > 1) {
        this.data[key].splice(paramIndex, 1)
      } else {
        this.$bvToast.toast(this.$t('message.atLeastOneParameter'), {
          variant: 'warning',
        })
      }
    },
    onChange(value) {
      // console.log(value)
    },
    onCategoryChange(index, formdata) {
      if (this.data?.serviceCateAndType?.[index]?.serviceCategory?.length === this.initServiceCategory?.length) {
        return
      }
      this.data.serviceCateAndType[index].serviceTypes = []
      this.data.serviceCateAndType[index].serviceSubTypes = []
      this.resetIndex += 1
      this.resetSubIndex += 1
    },
    onServiceTypeChange(paramIndex) {
      try {
        const sjson = JSON.stringify(this.data?.serviceCateAndType?.[paramIndex]?.serviceTypes.map(e => e.code))
        const isjson = JSON.stringify(this.initServiceTypes.map(e => e.code))
        const issjson = JSON.stringify(this.initServiceSubTypes.map(e => e.code))
        if (sjson === isjson || sjson === issjson) {
          return
        }
        this.data.serviceCateAndType[paramIndex].serviceSubTypes = []
        this.resetSubIndex += 1
      } catch (error) {
        // console.log('error:', error)
      }
    },
    onDepartmentChange(index) {
      this.data.departmentAndTeam[index].teamId = []
    },
    show() {
      OperatorRepository.show(this.$route.params.id)
        .then(response => {
          const data = response?.data?.data

          if (`${this.$route.query.isBot}` === 'true') {
            this.initData = { ...data }
          } else {
            // console.log('serviceCateAndType:', data.serviceCateAndType)
            // Transform snake_case to camelCase and map the data
            this.initData = {
              ...data,
              email: data.email,
              phone: data.phone,
              roleId: data.roleId,
              serviceCap: data.serviceCap?.toString() || '5',
              totalServiceCap: data.totalServiceCap?.toString() || '5',
              isAllowedOperatingService: data.serviceCateAndType?.length > 0 || data.projects?.length > 0 || data.isAutoAssignService,
              isAutoAssignService: data.isAutoAssignService || false,

              // Map projects
              projects: data.projects?.map(project => ({
                id: project.projectId,
                name: project.translationsObj || project.name,
                isActive: project.isActive,
                isDelete: project.isDelete,
                remark: project.remark,
                merchantId: project.merchantId,
                createdAt: project.createdAt,
                updatedAt: project.updatedAt,
                v: project.v,
                translations: project.translations,
              })) || [],

              // Map department and team
              departmentAndTeam: data.departmentAndTeam.length ? data.departmentAndTeam?.map(department => ({
                departmentId: department.departmentId,
                teamId: department.teams,
              })) : [{
                departmentId: '',
                teamId: [],
              }],

              // Map service categories and types
              serviceCateAndType: data.serviceCateAndType?.length
                ? [{
                  serviceCategory: data.serviceCateAndType?.map(category => ({
                    id: category.id,
                    name: category.name,
                    isDelete: category.isDelete,
                    isActive: true,
                    code: category.serviceCategoryCode,
                    description: category.description,
                    createdAt: category.createdAt,
                    updatedAt: category.updatedAt,
                    translations: category.translations,
                    translationsObj: category.translationsObj,
                  })),
                  serviceTypes: data.serviceCateAndType?.reduce((acc, category) => [...acc, ...(category.serviceTypes || [])?.map(type => ({
                    id: type.id,
                    name: type.name,
                    isDelete: type.isDelete,
                    isActive: type.isActive,
                    description: type.description,
                    serviceCategoryCode: type.serviceCategoryCode,
                    code: type.code,
                    createdAt: type.createdAt,
                    updatedAt: type.updatedAt,
                    translations: type.translations,
                    translationsObj: type.translationsObj,
                  }))], []).filter((type, index, self) => index === self.findIndex(t => t.code === type.code)),
                  serviceSubTypes: data.serviceCateAndType?.reduce((acc, category) => [
                    ...acc,
                    ...(category.serviceTypes || [])?.reduce((subAcc, type) => [
                      ...subAcc,
                      ...(type.serviceSubTypes || [])?.map(subType => ({
                        id: subType.id,
                        name: subType.name,
                        isDelete: subType.isDelete,
                        isActive: subType.isActive,
                        description: subType.description,
                        serviceTypeCode: type.code,
                        code: subType.code,
                        createdAt: subType.createdAt,
                        updatedAt: subType.updatedAt,
                        translations: subType.translations,
                        translationsObj: subType.translationsObj,
                      })),
                    ], []),
                  ], []).filter((type, index, self) => index === self.findIndex(t => t.code === type.code)),
                }]
                : [{
                  serviceCategory: null,
                  serviceTypes: [],
                  serviceSubTypes: [],
                }],
            }
          }

          this.initServiceCategory = this.initData.serviceCateAndType?.map(e => e.serviceCategory).flat()
          this.initServiceTypes = this.initData.serviceCateAndType?.map(e => e.serviceTypes).flat()
          this.initServiceSubTypes = this.initData.serviceCateAndType?.map(e => e.serviceSubTypes).flat()
          this.initialTotalServiceCap = this.initData.totalServiceCap
          this.data = { ...this.initData }
        })
        .catch(error => {
          console.error('Error fetching data:', error)
        })
    },
    validateForm() {
      this.$refs.editForm.validate().then(success => {
        if (success) {
          if (this.data.image) {
            this.uploadImage()
          } else {
            this.submit()
          }
        }
      })
    },
    async submit() {
      this.loading = true
      const payload = {
        // Base fields
        isActive: this.data.isActive,
        email: this.data.email,
        phone: this.data.phone,
        roleId: this.data.roleId,
        ...(this.data.newPassword ? { newPassword: this.data.newPassword } : {}),
        serviceCap: +this.data.serviceCap,
        totalServiceCap: +this.initialTotalServiceCap,
        isAutoAssignService: false,
        serviceCateAndType: [],
        projects: [],

        // Map department and team
        departmentAndTeam: this.data.departmentAndTeam?.map(item => ({
          departmentId: item.departmentId,
          teamId: Array.isArray(item.teamId) ? [...item.teamId] : [],
        })),

        // Add service related fields if isAllowedOperatingService is true
        ...(this.data.isAllowedOperatingService ? {
          totalServiceCap: +this.data.totalServiceCap,
          isAllowedOperatingService: true,
          isAutoAssignService: this.data.isAutoAssignService,

          // Map projects
          projects: this.data.projects?.map(project => ({
            projectId: project.id,
            name: project.translationsObj || project.name,
          })),

          // Map service categories and types
          serviceCateAndType: this.data.serviceCateAndType
            .filter(category => category.serviceCategory && category.serviceCategory.length)
            .map(category => {
              // Handle ALL_CATEGORY case
              if (category.serviceCategory.some(cat => cat.code === 'ALL_CATEGORY')) {
                return [{
                  serviceCategoryCode: 'ALL_CATEGORY',
                  serviceTypes: category.serviceTypes.some(type => type.code === 'ALL_TYPE')
                    ? [
                      {
                        code: 'ALL_TYPE',
                        serviceSubTypes: category.serviceSubTypes.some(subType => subType.code === 'ALL_SUBTYPE')
                          ? [{ code: 'ALL_SUBTYPE' }]
                          : (category.serviceSubTypes || [])
                            .map(subType => ({
                              code: subType.code,
                              name: subType.name,
                              serviceTypeCode: subType.serviceTypeCode,
                            })),
                      },
                    ]
                    : category.serviceTypes.map(type => ({
                      code: type.code,
                      name: type.name,
                      serviceSubTypes: category.serviceSubTypes.some(subType => subType.code === 'ALL_SUBTYPE')
                        ? [{ code: 'ALL_SUBTYPE' }]
                        : (category.serviceSubTypes || [])
                          .filter(subType => subType.serviceTypeCode === type.code)
                          .map(subType => ({
                            code: subType.code,
                            name: subType.name,
                            serviceTypeCode: subType.serviceTypeCode,
                          })),
                    })),
                }]
              }

              // Handle regular categories
              const categories = Array.isArray(category.serviceCategory)
                ? category.serviceCategory
                : [category.serviceCategory]

              return categories.map(cat => ({
                name: cat.name,
                serviceCategoryCode: cat.code,
                serviceTypes: category.serviceTypes.some(type => type.code === 'ALL_TYPE')
                  ? [
                    {
                      code: 'ALL_TYPE',
                      serviceSubTypes: category.serviceSubTypes.some(subType => subType.code === 'ALL_SUBTYPE')
                        ? [{ code: 'ALL_SUBTYPE' }]
                        : (category.serviceSubTypes || [])
                          .map(subType => ({
                            code: subType.code,
                            name: subType.name,
                            serviceTypeCode: subType.serviceTypeCode,
                          })),
                    },
                  ]
                  : (category.serviceTypes || [])
                    .filter(type => type.serviceCategoryCode === cat.code)
                    .map(type => ({
                      code: type.code,
                      name: type.name,
                      serviceSubTypes: category.serviceSubTypes.some(subType => subType.code === 'ALL_SUBTYPE')
                        ? [{ code: 'ALL_SUBTYPE' }]
                        : (category.serviceSubTypes || [])
                          .filter(subType => subType.serviceTypeCode === type.code)
                          .map(subType => ({
                            code: subType.code,
                            name: subType.name,
                            serviceTypeCode: subType.serviceTypeCode,
                          })),
                    })),
              }))
            }).flat(),
        } : {}),
      }

      // delete payload.isActive
      // if no projects field in payload put empty array
      if (!payload.projects) {
        payload.projects = []
      }

      try {
        await OperatorRepository.update(this.$route.params.id, payload)
        this.$router.push({ name: `list-${this.route}` })
      } catch (error) {
        if (error.response?.status === 422) {
          this.$refs.editForm.setErrors(error.response?.data?.message)
        }
      } finally {
        this.loading = false
      }
    },
    back() {
      this.$router.back()
    },
    uploadImage() {
      this.loading = true
      const formData = new FormData()
      formData.append('image', this.data.image)
      FileRepository.upload(formData)
        .then(response => {
          const data = response?.data?.data
          if (data) {
            this.data.mediaId = data.mediaId
            this.submit()
          }
        })
        .catch(() => {
          this.loading = false
        })
    },
  },
  setup() {
    const fields = formInput
    const botFields = formInputBot
    const permissionType = 'manage-users'
    const route = 'operator'
    return {
      fields,
      permissionType,
      route,
      botFields,
      languageEn,
      languageKm,
      languageZh,
      languageVi,
    }
  },
}
</script>

<style lang="scss" scoped>
  .compact-container {
    width: 100%;
    border: 1px solid rgba(47, 47, 47, 0.1);
    padding: 1rem;
    margin: 1rem;
    border-radius: 5px;
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .compact-card {
      display: flex;
      align-items: flex-start;
      gap: 1rem;
      .key,
      .value {
        flex: 1;
      }
    }

    .service-compact-card {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      flex: 1;
      width: 100%;

      .key {
        display: flex;
        align-items: center;
        gap: 1rem;

        span {
          flex: 1;
        }
      }

      .service-type-container {
        display: flex;
        gap: 1rem;

        span {
          flex: 1;
        }
      }
    }
  }

  .delete-btn {
    margin-top: 3.35rem;
  }

  .compact-container {
    width: 100%;
    border: 1px solid rgba(47, 47, 47, 0.1);
    padding: 1rem;
    margin: 1rem;
    border-radius: 5px;
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .compact-card {
      // margin: 1rem 0;
      display: flex;
      align-items: flex-start;
      gap: 1rem;
      .key,
      .value {
        flex: 1;
      }
    }
  }

  .delete-btn {
    margin-top: 3.35rem;
  }

  .operation-container {
    width: 100%;
    // border: 1px solid rgba(47, 47, 47, 0.1);
    padding: 1rem;
    // margin: 1rem;
    border-radius: 5px;
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .col-6 {
      flex: none;
      padding: 0;
    }

    .compact-container {
      margin: 0;
      width: unset;
    }
  }

  .switch-container {
    display: flex;
    gap: 1rem;

    &.operator {
      display: flex;
      flex-direction: column;
    }
  }
</style>
